import { createOidc } from 'oidc-spa/core';
import { z } from 'zod';
import type { Oidc } from 'oidc-spa/core';
import type { Store } from 'vuex';
import type VueRouter from 'vue-router';
import {
  authUserService,
  INACTIVE_USER_TIME_THRESHOLD_IN_MILLISECONDS,
  INACTIVE_USER_TIME_THRESHOLD_IN_MINUTES
} from "@/services/auth-user";
import {baseNameRoutes} from "@/router/paths";

const PUBLIC_ROUTE_PATTERNS = [
  /^\/re\/[^/]+$/,
  /^\/hip\/[^/]+(\/print)?$/,
  /^\/ereceta\/registro$/,
  /^\/firma\/[^/]+$/,
  /^\/reset\/[^/]+\/[^/]+$/,
  /^\/activar\/[^/]+\/[^/]+$/,
  /^\/activateprofaspatient\/[^/]+\/[^/]+$/,
  /^\/linkPacienteProfesional\/[^/]+$/,
  /^\/ico\/[^/]+\/[^/]+$/,
  /^\/mdk\/[^/]+\/[^/]+$/,
  /^\/landing\/[^/]+$/,
  /^\/pay\/[^/]+\/[^/]+\/[^/]+$/,
  /^\/google-authenticator-redirect$/,
];

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_ROUTE_PATTERNS.some(pattern => pattern.test(pathname));
}

export interface OidcPluginOptions {
  issuerUri: string;
  clientId: string;
  store: Store<any>;
  router: VueRouter;
  onReady?: () => void;
}

export interface VueOidcInstance {
  ready: boolean;
  authenticated: boolean;
  userName?: string;
  fullName?: string;
  email?: string;
  token?: string;
  tokenParsed?: any;
  idToken?: string;
  idTokenParsed?: any;
  refreshToken?: string;
  realmAccess?: { roles?: string[] };
  resourceAccess?: any;
  subject?: string;
  login: (options?: { doesCurrentHrefRequiresAuth?: boolean; extraQueryParams?: any; redirectUrl?: string }) => Promise<void>;
  logout: (options?: { redirectTo?: 'home' | 'current' | 'current page'; url?: string }) => void;
  getTokens: () => Promise<{ accessToken: string; idToken: string; refreshToken?: string }>;
  getDecodedIdToken: () => any;
  updateToken: () => Promise<void>;
  goToAuthServer: (options?: { extraQueryParams?: any }) => void;
  oidc: Oidc<any> | null;
}

const decodedIdTokenSchema = z.object({
  sub: z.string(),
  preferred_username: z.string().optional(),
  name: z.string().optional(),
  email: z.string().email().optional(),
  email_verified: z.boolean().optional(),
  realm_access: z.object({ 
    roles: z.array(z.string()) 
  }).optional(),
  resource_access: z.any().optional(),
  password_expiration_date: z.union([z.string(), z.number()]).optional(),
});

let installed = false;

export default {
  install: async function (Vue: any, options: OidcPluginOptions) {
    if (installed) return;
    installed = true;

    const { issuerUri, clientId, store, router, onReady } = options;

    const vueOidcInstance: VueOidcInstance = Vue.observable({
      ready: false,
      authenticated: false,
      userName: undefined,
      fullName: undefined,
      email: undefined,
      token: undefined,
      tokenParsed: undefined,
      idToken: undefined,
      idTokenParsed: undefined,
      refreshToken: undefined,
      realmAccess: undefined,
      resourceAccess: undefined,
      subject: undefined,
      login: async () => {},
      logout: () => {},
      getTokens: async () => ({ accessToken: '', idToken: '' }),
      getDecodedIdToken: () => ({}),
      updateToken: async () => {},
      goToAuthServer: () => {},
      oidc: null,
    });

    Object.defineProperty(Vue.prototype, '$oidc', {
      get() {
        return vueOidcInstance;
      }
    });

    Object.defineProperty(Vue.prototype, '$keycloak', {
      get() {
        return vueOidcInstance;
      }
    });

    try {
      const currentAuthUserRaw = window.localStorage.getItem('auth_ehealth');
      const currentAuthUser = currentAuthUserRaw ? JSON.parse(currentAuthUserRaw) : null;

      const oidc = await createOidc({
        issuerUri,
        clientId,
        decodedIdTokenSchema,
        debugLogs: false,
        ...(currentAuthUser?.access_token && {
          extraQueryParams: () => ({})
        })
      });

      vueOidcInstance.oidc = oidc;

      if (!oidc.isUserLoggedIn) {
        const currentPath = window.location.pathname;
        const isPublic = isPublicPath(currentPath);
        
        if (!isPublic) {
          await (oidc as any).login({
            doesCurrentHrefRequiresAuth: true
          });
          return;
        }
        
        vueOidcInstance.ready = true;
        vueOidcInstance.authenticated = false;
        
        if (onReady) {
          onReady();
        }
        return;
      }

      const tokens = await oidc.getTokens();
      const decodedIdToken = oidc.getDecodedIdToken();

      vueOidcInstance.ready = true;
      vueOidcInstance.authenticated = true;
      vueOidcInstance.token = tokens.accessToken;
      vueOidcInstance.idToken = tokens.idToken;
      vueOidcInstance.refreshToken = tokens.refreshToken;
      vueOidcInstance.tokenParsed = decodedIdToken;
      vueOidcInstance.idTokenParsed = decodedIdToken;
      vueOidcInstance.userName = decodedIdToken.preferred_username;
      vueOidcInstance.fullName = decodedIdToken.name;
      vueOidcInstance.email = decodedIdToken.email;
      vueOidcInstance.subject = decodedIdToken.sub;
      vueOidcInstance.realmAccess = decodedIdToken.realm_access;
      vueOidcInstance.resourceAccess = decodedIdToken.resource_access;

      vueOidcInstance.login = async (loginOptions) => {
        await (oidc as any).login({
          doesCurrentHrefRequiresAuth: loginOptions?.doesCurrentHrefRequiresAuth ?? false,
          extraQueryParams: loginOptions?.extraQueryParams,
          ...(loginOptions?.redirectUrl && { redirectUrl: loginOptions.redirectUrl })
        });
      };

      vueOidcInstance.logout = (logoutOptions) => {
        let redirectTo: 'home' | 'current page' | 'specific url' = 'home';
        if (logoutOptions?.redirectTo === 'current' || logoutOptions?.redirectTo === 'current page') {
          redirectTo = 'current page';
        } else if (logoutOptions?.redirectTo) {
          redirectTo = logoutOptions.redirectTo as any;
        }
        
        oidc.logout({ 
          redirectTo,
          ...(logoutOptions?.url && { url: logoutOptions.url })
        });
      };

      vueOidcInstance.getTokens = async () => {
        return await oidc.getTokens();
      };

      vueOidcInstance.getDecodedIdToken = () => {
        return oidc.getDecodedIdToken();
      };

      vueOidcInstance.updateToken = async () => {
        const newTokens = await oidc.getTokens();
        vueOidcInstance.token = newTokens.accessToken;
        vueOidcInstance.idToken = newTokens.idToken;
        vueOidcInstance.refreshToken = newTokens.refreshToken;
        
        authUserService.updateTokens(newTokens.accessToken, newTokens.refreshToken);
      };

      vueOidcInstance.goToAuthServer = (options) => {
        (oidc as any).goToAuthServer({
          extraQueryParams: options?.extraQueryParams || {}
        });
      };

      (vueOidcInstance as any).logoutFn = () => {
        vueOidcInstance.logout({ redirectTo: 'home' });
      };

      if (onReady) {
        onReady();
      }

      authUserService.updateTokens(tokens.accessToken, tokens.refreshToken);

      const tokenRefreshInterval = setInterval(async () => {
        try {
          await vueOidcInstance.updateToken();
        } catch (error) {
          clearInterval(tokenRefreshInterval);
          authUserService.remove();
          store.dispatch('app/loadConfigFromBrand');
          router.push({ name: baseNameRoutes.login });
        }
      }, INACTIVE_USER_TIME_THRESHOLD_IN_MILLISECONDS);
    } catch (error) {
      throw error;
    }
  }
};

